import React, { useEffect, useState } from 'react';
import { Download, FileUp, X } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { Button } from '../../../shared/components/Button';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import type { PurchasePayment } from '../api';
import { downloadPaymentProof } from '../api';
import { formatDate, formatDateTime, formatMoney, hasAvailableAction } from './format';

type PaymentAction = 'schedule' | 'mark-paid' | 'reconcile' | 'cancel' | 'proof';

interface Props {
  payments: PurchasePayment[];
  canCreate: boolean;
  canManage: boolean;
  submitting?: boolean;
  error?: string | null;
  onCreate: (input: { dueDate: string; amount?: number; idempotencyKey: string }) => Promise<void>;
  onSchedule: (id: string, scheduledFor: string) => Promise<void>;
  onMarkPaid: (id: string, paymentReference: string, paidAt?: string) => Promise<void>;
  onReconcile: (id: string, reconciliationReference: string, reconciledAt?: string) => Promise<void>;
  onCancel: (id: string, justification: string) => Promise<void>;
  onUploadProof: (id: string, file: File) => Promise<void>;
}

export function PaymentsPanel({
  payments,
  canCreate,
  canManage,
  submitting,
  error,
  onCreate,
  onSchedule,
  onMarkPaid,
  onReconcile,
  onCancel,
  onUploadProof,
}: Props) {
  const [showCreate, setShowCreate] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [amount, setAmount] = useState('');
  const [target, setTarget] = useState<{ payment: PurchasePayment; action: PaymentAction } | null>(null);
  const [value, setValue] = useState('');
  const [date, setDate] = useState('');
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (!target) {
      setValue('');
      setDate('');
      setFile(null);
    }
  }, [target]);

  async function confirmAction() {
    if (!target) return;
    const { payment, action } = target;
    if (action === 'schedule') await onSchedule(payment.id, date);
    if (action === 'mark-paid') await onMarkPaid(payment.id, value.trim(), date || undefined);
    if (action === 'reconcile') await onReconcile(payment.id, value.trim(), date || undefined);
    if (action === 'cancel') await onCancel(payment.id, value.trim());
    if (action === 'proof' && file) await onUploadProof(payment.id, file);
    setTarget(null);
  }

  const modalLabels: Record<PaymentAction, { title: string; field?: string; date?: string; confirm: string }> = {
    schedule: { title: 'Agendar pagamento', date: 'Agendado para *', confirm: 'Agendar' },
    'mark-paid': { title: 'Registrar pagamento', field: 'Referência do pagamento *', date: 'Pago em', confirm: 'Registrar pagamento' },
    reconcile: { title: 'Conciliar pagamento', field: 'Referência da conciliação *', date: 'Conciliado em', confirm: 'Conciliar' },
    cancel: { title: 'Cancelar pagamento', field: 'Justificativa *', confirm: 'Cancelar pagamento' },
    proof: { title: 'Enviar comprovante', confirm: 'Enviar arquivo' },
  };

  const actionIsValid = target?.action === 'proof'
    ? Boolean(file)
    : target?.action === 'schedule'
      ? Boolean(date)
      : value.trim().length >= (target?.action === 'cancel' ? 5 : 2);

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}
      {canCreate && (
        <div className="flex justify-end">
          <Button onClick={() => setShowCreate((open) => !open)}>{showCreate ? 'Cancelar inclusão' : 'Adicionar pagamento'}</Button>
        </div>
      )}
      {showCreate && (
        <form
          className="grid gap-4 rounded-md border border-gray-200 bg-gray-50 p-4 md:grid-cols-[1fr_1fr_auto]"
          onSubmit={(event) => {
            event.preventDefault();
            if (!dueDate) return;
            void onCreate({
              dueDate,
              ...(amount.trim() ? { amount: Number(amount.replace(',', '.')) } : {}),
              idempotencyKey: crypto.randomUUID(),
            }).then(() => setShowCreate(false));
          }}
        >
          <TextInput label="Vencimento *" type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
          <TextInput label="Valor" type="number" min="0.01" step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} />
          <Button type="submit" className="self-center" loading={submitting} disabled={!dueDate}>Criar pagamento</Button>
        </form>
      )}

      {payments.length === 0 ? (
        <p className="text-sm text-gray-500">Nenhum pagamento cadastrado.</p>
      ) : (
        <div className="space-y-3">
          {payments.map((payment) => {
            const may = (action: string) => canManage && hasAvailableAction(payment.availableActions, action);
            const statusLabels: Record<string, string> = { pending: 'Pendente', scheduled: 'Agendado', paid: 'Pago', reconciled: 'Conciliado', cancelled: 'Cancelado' };
            return (
              <article key={payment.id} className="rounded-md border border-gray-200 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <strong className="text-gray-900">{formatMoney(payment.amount)}</strong>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{statusLabels[payment.status] ?? 'Em processamento'}</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600">Vencimento: {formatDate(payment.dueDate)}</p>
                    {payment.scheduledFor && <p className="text-xs text-gray-500">Agendado: {formatDateTime(payment.scheduledFor)}</p>}
                    {payment.paidAt && <p className="text-xs text-gray-500">Pago: {formatDateTime(payment.paidAt)} · {payment.paymentReference}</p>}
                    {payment.reconciledAt && <p className="text-xs text-gray-500">Conciliado: {formatDateTime(payment.reconciledAt)} · {payment.reconciliationReference}</p>}
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    {may('schedule') && <Button size="sm" variant="secondary" onClick={() => setTarget({ payment, action: 'schedule' })}>Agendar</Button>}
                    {may('mark_paid') && <Button size="sm" onClick={() => setTarget({ payment, action: 'mark-paid' })}>Marcar pago</Button>}
                    {may('upload_proof') && <Button size="sm" variant="secondary" icon={<FileUp className="h-3.5 w-3.5" />} onClick={() => setTarget({ payment, action: 'proof' })}>Comprovante</Button>}
                    {(payment.proofUrl || payment.proofFileName) && <Button size="sm" variant="ghost" icon={<Download className="h-3.5 w-3.5" />} onClick={() => void downloadPaymentProof(payment.id)}>Baixar</Button>}
                    {may('reconcile') && <Button size="sm" onClick={() => setTarget({ payment, action: 'reconcile' })}>Conciliar</Button>}
                    {may('cancel') && <Button size="sm" variant="danger" onClick={() => setTarget({ payment, action: 'cancel' })}>Cancelar</Button>}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {target && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label={modalLabels[target.action].title}>
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <header className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h3 className="text-lg font-semibold text-gray-900">{modalLabels[target.action].title}</h3>
              <button type="button" aria-label="Fechar" onClick={() => setTarget(null)}><X className="h-5 w-5" /></button>
            </header>
            <div className="space-y-4 px-5 py-5">
              {target.action === 'proof' ? (
                <input
                  aria-label="Arquivo do comprovante"
                  data-testid="payment-proof-file"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                  className="block w-full rounded-md border border-gray-300 p-2"
                />
              ) : target.action === 'cancel' ? (
                <Textarea label={modalLabels[target.action].field as string} value={value} onChange={(event) => setValue(event.target.value)} />
              ) : (
                <>
                  {modalLabels[target.action].field && <TextInput label={modalLabels[target.action].field as string} value={value} onChange={(event) => setValue(event.target.value)} />}
                  {modalLabels[target.action].date && <TextInput label={modalLabels[target.action].date as string} type="datetime-local" value={date} onChange={(event) => setDate(event.target.value)} />}
                </>
              )}
            </div>
            <footer className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
              <Button variant="secondary" onClick={() => setTarget(null)}>Voltar</Button>
              <Button variant={target.action === 'cancel' ? 'danger' : 'primary'} loading={submitting} disabled={!actionIsValid} onClick={() => void confirmAction()}>{modalLabels[target.action].confirm}</Button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
